import type {
  ActivityEntry,
  AdminReservationDetail,
  AdminReservationFilters,
  AdminReservationList,
  AdminRsvpDetail,
  AdminRsvpFilters,
  AdminRsvpList,
  DoorCheckInInput,
  DoorList,
  DoorListRow,
  ManualRsvpInput,
  RsvpResponse,
  AdminGuestbookEntry,
  AdminGuestbookList,
  GuestbookStatus,
  AdminRoomType,
  AdminRoomTypeInput,
  AdminSettings,
  AdminUserCredential,
  AdminUserInput,
  AdminUserUpdate,
  AmenityDto,
  AmenityInput,
  CheckoutResponse,
  DashboardStats,
  DigestSendResult,
  IsoDate,
  ManualReservationInput,
  OccupancyGrid,
  Paginated,
  PublicUser,
  RoomImageDto,
  UploadPresignRequest,
  UploadPresignResponse,
} from '@brandonstrong/types';
import { API_URL, qs, request } from './api';

const get = <T>(path: string) => request<T>(path, { bearer: true });
const send = <T>(method: string, path: string, body?: unknown) =>
  request<T>(path, { method, body, bearer: true });
type Order = Array<{ id: string; sortOrder: number }>;
const filtersQs = (f: AdminReservationFilters | AdminRsvpFilters) =>
  qs(f as Record<string, string | number | undefined>);

/** Everything under /v1/admin. Bearer token attached automatically. */
export const adminApi = {
  dashboard: () => get<DashboardStats>('/v1/admin/dashboard'),
  occupancy: (from?: IsoDate, to?: IsoDate) =>
    get<OccupancyGrid>(`/v1/admin/occupancy${qs({ from, to })}`),
  activity: (page = 1, pageSize = 50) =>
    get<Paginated<ActivityEntry>>(`/v1/admin/activity${qs({ page, pageSize })}`),
  digest: {
    send: () => send<DigestSendResult>('POST', '/v1/admin/digest/send'),
  },
  users: {
    list: () => get<PublicUser[]>('/v1/admin/users'),
    create: (body: AdminUserInput) => send<AdminUserCredential>('POST', '/v1/admin/users', body),
    update: (id: string, body: AdminUserUpdate) =>
      send<PublicUser>('PATCH', `/v1/admin/users/${id}`, body),
    resetPassword: (id: string) =>
      send<AdminUserCredential>('POST', `/v1/admin/users/${id}/reset-password`),
  },
  guestbook: {
    list: (status?: GuestbookStatus, page = 1, pageSize = 50) =>
      get<AdminGuestbookList>(`/v1/admin/guestbook${qs({ status, page, pageSize })}`),
    hide: (id: string) => send<AdminGuestbookEntry>('POST', `/v1/admin/guestbook/${id}/hide`),
    restore: (id: string) => send<AdminGuestbookEntry>('POST', `/v1/admin/guestbook/${id}/restore`),
    remove: (id: string) => send<{ deleted: boolean }>('DELETE', `/v1/admin/guestbook/${id}`),
  },
  settings: {
    get: () => get<AdminSettings>('/v1/admin/settings'),
    update: (patch: Partial<AdminSettings>) =>
      send<AdminSettings>('PUT', '/v1/admin/settings', patch),
  },
  rooms: {
    list: (archived = false) =>
      get<AdminRoomType[]>(`/v1/admin/rooms${qs({ archived: archived ? 1 : undefined })}`),
    get: (id: string) => get<AdminRoomType>(`/v1/admin/rooms/${id}`),
    create: (body: AdminRoomTypeInput) => send<AdminRoomType>('POST', '/v1/admin/rooms', body),
    update: (id: string, body: Partial<AdminRoomTypeInput>) =>
      send<AdminRoomType>('PUT', `/v1/admin/rooms/${id}`, body),
    reorder: (items: Order) => send<AdminRoomType[]>('PUT', '/v1/admin/rooms/reorder', items),
    archive: (id: string) => send<AdminRoomType>('POST', `/v1/admin/rooms/${id}/archive`),
    restore: (id: string) => send<AdminRoomType>('POST', `/v1/admin/rooms/${id}/restore`),
  },
  images: {
    presign: (roomId: string, body: UploadPresignRequest) =>
      send<UploadPresignResponse>('POST', `/v1/admin/rooms/${roomId}/images/presign`, body),
    add: (
      roomId: string,
      body: { storageKey?: string; url: string; altText?: string | null; isPrimary?: boolean },
    ) => send<RoomImageDto>('POST', `/v1/admin/rooms/${roomId}/images`, body),
    update: (
      roomId: string,
      imageId: string,
      body: { altText?: string | null; isPrimary?: boolean },
    ) => send<RoomImageDto>('PATCH', `/v1/admin/rooms/${roomId}/images/${imageId}`, body),
    reorder: (roomId: string, items: Order) =>
      send<RoomImageDto[]>('PUT', `/v1/admin/rooms/${roomId}/images/reorder`, items),
    remove: (roomId: string, imageId: string) =>
      send<{ deleted: boolean }>('DELETE', `/v1/admin/rooms/${roomId}/images/${imageId}`),
  },
  amenities: {
    list: () => get<AmenityDto[]>('/v1/admin/amenities'),
    create: (body: AmenityInput) => send<AmenityDto>('POST', '/v1/admin/amenities', body),
    update: (id: string, body: Partial<AmenityInput>) =>
      send<AmenityDto>('PUT', `/v1/admin/amenities/${id}`, body),
    reorder: (items: Order) => send<AmenityDto[]>('PUT', '/v1/admin/amenities/reorder', items),
    remove: (id: string) => send<{ deleted: boolean }>('DELETE', `/v1/admin/amenities/${id}`),
  },
  reservations: {
    list: (filters: AdminReservationFilters) =>
      get<AdminReservationList>(`/v1/admin/reservations${filtersQs(filters)}`),
    detail: (id: string) => get<AdminReservationDetail>(`/v1/admin/reservations/${id}`),
    cancel: (id: string, reason: string) =>
      send<AdminReservationDetail & { notice: string }>(
        'POST',
        `/v1/admin/reservations/${id}/cancel`,
        { reason },
      ),
    notes: (id: string, adminNotes: string | null) =>
      send<AdminReservationDetail>('PATCH', `/v1/admin/reservations/${id}/notes`, { adminNotes }),
    resend: (id: string) =>
      send<{ sent: boolean; reason?: string }>(
        'POST',
        `/v1/admin/reservations/${id}/resend-confirmation`,
      ),
    manual: (body: ManualReservationInput) =>
      send<CheckoutResponse>('POST', '/v1/admin/reservations/manual', body),
    exportUrl: (filters: AdminReservationFilters) =>
      `${API_URL}/v1/admin/reservations/export.csv${filtersQs(filters)}`,
  },
  rsvps: {
    list: (filters: AdminRsvpFilters) => get<AdminRsvpList>(`/v1/admin/rsvps${filtersQs(filters)}`),
    detail: (id: string) => get<AdminRsvpDetail>(`/v1/admin/rsvps/${id}`),
    cancel: (id: string, reason: string) =>
      send<AdminRsvpDetail & { notice: string }>('POST', `/v1/admin/rsvps/${id}/cancel`, { reason }),
    notes: (id: string, adminNotes: string | null) =>
      send<AdminRsvpDetail>('PATCH', `/v1/admin/rsvps/${id}/notes`, { adminNotes }),
    resend: (id: string) =>
      send<{ sent: boolean; reason?: string }>('POST', `/v1/admin/rsvps/${id}/resend-confirmation`),
    manual: (body: ManualRsvpInput) => send<RsvpResponse>('POST', '/v1/admin/rsvps/manual', body),
    doorList: () => get<DoorList>('/v1/admin/rsvps/door-list'),
    doorCheckIn: (body: DoorCheckInInput) =>
      send<DoorListRow>('POST', '/v1/admin/rsvps/door-list/check-in', body),
    exportUrl: (filters: AdminRsvpFilters) => `${API_URL}/v1/admin/rsvps/export.csv${filtersQs(filters)}`,
    doorListCsvUrl: () => `${API_URL}/v1/admin/rsvps/door-list.csv`,
  },
};
